import type { LatencyProbeReport } from './latencyProbeContracts';
import type { WebCodecsPaintSample } from './native-video/WebCodecsPaintSample';

/**
 * Turns the most recent WebCodecs paint into a calibrated latency report, so a canvas
 * viewer rides the same `latencyProbe` wire, `CalibratedLatencyTracker`, log lines and
 * overlay as an Electron viewer.
 *
 * `rtpTimestamp` carries the frame's `sentUs` (the host sender's clock); the host maps it to
 * its wall clock through the sender's anchor. A paint older than `maxAgeMs` is not reported:
 * after a fallback to conventional video the last canvas frame must not keep describing the
 * stream.
 */
export class WebCodecsLatencyReporter {
  private latest: WebCodecsPaintSample | null = null;

  constructor(private readonly maxAgeMs = 3_000) {}

  observe(sample: WebCodecsPaintSample): void {
    if (!Number.isFinite(sample.sentUs) || !Number.isFinite(sample.paintWallMs)) return;
    this.latest = sample;
  }

  /** True while a fresh paint exists; the monitor then prefers this report over rVFC. */
  hasFreshSample(nowMs: number): boolean {
    return this.latest !== null && nowMs - this.latest.paintWallMs <= this.maxAgeMs;
  }

  reportFor(probeId: number, nowMs: number, pathEpoch: number | undefined): LatencyProbeReport | undefined {
    const sample = this.latest;
    if (!sample || !this.hasFreshSample(nowMs)) return undefined;
    return {
      type: 'latencyProbe',
      role: 'report',
      probeId,
      rtpTimestamp: sample.sentUs,
      guestDecodeWallMs: sample.paintWallMs,
      source: 'webcodecs-sent',
      observationAgeMs: Math.max(0, nowMs - sample.paintWallMs),
      timingSource: 'callback-time',
      ...(pathEpoch === undefined ? {} : { pathEpoch }),
    };
  }

  clear(): void { this.latest = null; }
}
